import { IconCaretDownFilled, IconCaretUpFilled, type IconProps } from '@tabler/icons-react-native';
import { Image, type ImageProps } from 'expo-image';
import { cloneElement, useId, useState, type ReactElement, type ReactNode } from 'react';
import { Platform, Pressable } from 'react-native';

import { Box, Text, useTheme } from 'app/shopify/theme';

export type AccordionItemState = 'enabled' | 'hover' | 'pressed' | 'focus' | 'disabled';

export interface AccordionItemProps {
  title: string;
  description?: string;
  children?: ReactNode;
  leadingIcon?: ReactElement<IconProps>;
  /** A 40px thumbnail takes precedence over the leading icon. */
  thumbnailSource?: ImageProps['source'];
  expanded?: boolean;
  defaultExpanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  disabled?: boolean;
  /** Preview a visual state in Storybook; enabled uses real interaction. */
  state?: AccordionItemState;
  accessibilityLabel?: string;
  testID?: string;
}

export function AccordionItem({
  title,
  description,
  children,
  leadingIcon,
  thumbnailSource,
  expanded,
  defaultExpanded = false,
  onExpandedChange,
  disabled = false,
  state = 'enabled',
  accessibilityLabel,
  testID,
}: AccordionItemProps) {
  const theme = useTheme();
  const id = useId();
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const isExpanded = expanded ?? internalExpanded;
  const isDisabled = disabled || state === 'disabled';
  const iconSize = Number(theme.tokens.sizing['sizing.icon.default']);
  const thumbnailSize = Number(theme.tokens.sizing['sizing.general.x10']);
  const inset = theme.spacing['spacing.general.x4'];
  const Caret = isExpanded ? IconCaretUpFilled : IconCaretDownFilled;

  function toggle() {
    if (isDisabled) return;
    const next = !isExpanded;
    if (expanded === undefined) setInternalExpanded(next);
    onExpandedChange?.(next);
  }

  return (
    <Box testID={testID} width="100%">
      <Pressable
        nativeID={`${id}-trigger`}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? (description ? `${title}. ${description}` : title)}
        accessibilityState={{ expanded: isExpanded, disabled: isDisabled }}
        aria-controls={Platform.OS === 'web' && isExpanded ? `${id}-content` : undefined}
        disabled={isDisabled}
        onPress={toggle}
        onHoverIn={() => setHovered(true)}
        onHoverOut={() => setHovered(false)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        style={{ marginHorizontal: -inset, paddingHorizontal: inset, borderRadius: theme.borderRadii['radius.md'] }}
      >
        {({ pressed }) => {
          const showPressed = !isDisabled && (state === 'pressed' || (state === 'enabled' && pressed));
          const showHover = !isDisabled && (state === 'hover' || (state === 'enabled' && hovered));
          const showFocus = !isDisabled && (focused || state === 'focus');

          return (
            <>
              <Box
                pointerEvents="none"
                position="absolute"
                top={0}
                right={0}
                bottom={0}
                left={0}
                borderRadius="radius.md"
                backgroundColor={
                  showPressed ? 'state-layer.press-brand' : showHover ? 'state-layer.hover-brand' : undefined
                }
                borderColor="border.focused"
                borderWidth={showFocus ? theme.borderWidths['border-width.emphasized'] : 0}
              />
              <Box
                flexDirection="row"
                alignItems="center"
                gap="spacing.general.x4"
                paddingVertical="spacing.general.x4"
              >
                {thumbnailSource || leadingIcon ? (
                  <Box
                    accessible={false}
                    accessibilityElementsHidden
                    importantForAccessibility="no-hide-descendants"
                    aria-hidden
                    pointerEvents="none"
                    flexShrink={0}
                    width={thumbnailSource ? thumbnailSize : iconSize}
                    height={thumbnailSource ? thumbnailSize : iconSize}
                    alignItems="center"
                    justifyContent="center"
                    borderRadius={thumbnailSource ? 'radius.sm' : 'radius.none'}
                    overflow="hidden"
                  >
                    {thumbnailSource ? (
                      <>
                        <Image
                          source={thumbnailSource}
                          contentFit="cover"
                          style={{ width: thumbnailSize, height: thumbnailSize }}
                        />
                        <Box
                          position="absolute"
                          top={0}
                          right={0}
                          bottom={0}
                          left={0}
                          borderRadius="radius.sm"
                          borderColor="border.secondary"
                          borderWidth={theme.borderWidths['border-width.subtle']}
                        />
                      </>
                    ) : leadingIcon ? (
                      cloneElement(leadingIcon, {
                        size: iconSize,
                        color: theme.colors[isDisabled ? 'icon.disabled' : 'icon.primary'],
                        strokeWidth: 2,
                      })
                    ) : null}
                  </Box>
                ) : null}
                <Box flex={1} minWidth={0} gap="spacing.general.x1">
                  <Text variant="bodyLargeStrong" color={isDisabled ? 'text.disabled' : 'text.primary'}>
                    {title}
                  </Text>
                  {description ? (
                    <Text variant="bodySmall" color={isDisabled ? 'text.disabled' : 'text.secondary'}>
                      {description}
                    </Text>
                  ) : null}
                </Box>
                <Box
                  accessible={false}
                  accessibilityElementsHidden
                  importantForAccessibility="no-hide-descendants"
                  aria-hidden
                  pointerEvents="none"
                  width={iconSize}
                  height={iconSize}
                  flexShrink={0}
                  alignItems="center"
                  justifyContent="center"
                >
                  <Caret
                    size={Number(theme.tokens.sizing['sizing.icon.sm'])}
                    color={theme.colors[isDisabled ? 'icon.disabled' : 'icon.secondary']}
                  />
                </Box>
              </Box>
            </>
          );
        }}
      </Pressable>
      {isExpanded ? (
        <Box
          nativeID={`${id}-content`}
          role={Platform.OS === 'web' ? 'region' : undefined}
          aria-labelledby={Platform.OS === 'web' ? `${id}-trigger` : undefined}
          backgroundColor="surface.primary-neutral"
          paddingBottom="spacing.general.x4"
        >
          {children}
        </Box>
      ) : null}
    </Box>
  );
}
